import { useState, useRef, useEffect, useCallback } from 'react';

import useGetFetchConfig from '@/api/useGetFetchConfig';

export interface PlaybackState {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  playbackRate: number;
}

export interface PlaybackController {
  state: PlaybackState;
  audioRef: React.RefObject<HTMLAudioElement>;
  togglePlayPause: () => void;
  seekTo: (time: number) => void;
  skipForward: (seconds?: number) => void;
  skipBackward: (seconds?: number) => void;
  setPlaybackRate: (rate: number) => void;
  getCurrentProgress: () => number;
}

interface UsePlaybackControllerProps {
  audioUrl?: string;
  onTimeUpdate?: (time: number) => void;
}

export const usePlaybackController = ({ audioUrl, onTimeUpdate }: UsePlaybackControllerProps): PlaybackController => {
  const [state, setState] = useState<PlaybackState>({
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    playbackRate: 1,
  });

  const audioRef = useRef<HTMLAudioElement>(null);
  const [streamUrl, setStreamUrl] = useState<string | null>(null);
  const { fetchConfigGET } = useGetFetchConfig();

  useEffect(() => {
    if (!audioUrl) return;

    let isCancelled = false;

    const getStreamingUrl = async () => {
      try {
        const tokenUrl = audioUrl.replace('/audio/full', '/audio/token');

        const response = await fetch(tokenUrl, fetchConfigGET);
        if (!response.ok) {
          throw new Error(`Failed to get stream token: ${response.status} ${response.statusText}`);
        }

        const { token } = await response.json();
        const streamingUrl = `${audioUrl}?token=${token}`;

        if (!isCancelled) {
          setStreamUrl(streamingUrl);
        }
      } catch (error) {
        console.error('Failed to get streaming URL:', error);
      }
    };

    getStreamingUrl();

    return () => {
      isCancelled = true;
    };
  }, [audioUrl, fetchConfigGET]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !streamUrl) return;

    if (audio.src !== streamUrl) {
      audio.src = streamUrl;
    }

    const handleLoadedMetadata = () => {
      setState((prev) => ({ ...prev, duration: audio.duration }));
    };

    const handleEnded = () => {
      stopTimeUpdates();
      setState((prev) => ({ ...prev, isPlaying: false, currentTime: 0 }));
    };

    const handleError = (e: Event) => {
      const audio = e.target as HTMLAudioElement;
      const error = audio.error;

      console.error('Failed to load audio file:', {
        streamUrl,
        originalUrl: audioUrl,
        errorCode: error?.code,
        errorMessage: error?.message,
        networkState: audio.networkState,
        readyState: audio.readyState,
        currentSrc: audio.currentSrc,
      });
    };

    const startTimeUpdates = () => {
      const intervalId = setInterval(() => {
        if (audio && !audio.paused && !audio.ended) {
          const currentAudioTime = audio.currentTime;
          setState((prev) => ({ ...prev, currentTime: currentAudioTime }));
          onTimeUpdate?.(currentAudioTime);
        } else {
          clearInterval(intervalId);
        }
      }, 100);

      animationFrameIdRef.current = intervalId;
    };

    const stopTimeUpdates = () => {
      if (animationFrameIdRef.current) {
        clearInterval(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }
    };

    startAnimationUpdatesRef.current = startTimeUpdates;
    stopAnimationUpdatesRef.current = stopTimeUpdates;

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      stopTimeUpdates();
    };
  }, [streamUrl, audioUrl, onTimeUpdate, audioRef.current]);

  const animationFrameIdRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startAnimationUpdatesRef = useRef<() => void>();
  const stopAnimationUpdatesRef = useRef<() => void>();

  const togglePlayPause = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (state.isPlaying) {
      audio.pause();
      setState((prev) => ({ ...prev, isPlaying: false }));
    } else {
      try {
        await audio.play();
        setState((prev) => ({ ...prev, isPlaying: true }));

        const updateTime = () => {
          if (audio && !audio.paused && !audio.ended) {
            const currentAudioTime = audio.currentTime;
            setState((prev) => ({ ...prev, currentTime: currentAudioTime }));
            onTimeUpdate?.(currentAudioTime);
            requestAnimationFrame(updateTime);
          }
        };

        requestAnimationFrame(updateTime);
      } catch (error) {
        console.error('Failed to play audio:', error);
        setState((prev) => ({ ...prev, isPlaying: false }));
      }
    }
  }, [state.isPlaying, onTimeUpdate]);

  const seekTo = useCallback(
    (time: number) => {
      const audio = audioRef.current;
      if (!audio) return;

      const clampedTime = Math.max(0, Math.min(time, state.duration));
      audio.currentTime = clampedTime;
      setState((prev) => ({ ...prev, currentTime: clampedTime }));
      onTimeUpdate?.(clampedTime);
    },
    [state.duration, onTimeUpdate],
  );

  const skipForward = useCallback(
    (seconds: number = 10) => {
      seekTo(state.currentTime + seconds);
    },
    [state.currentTime, seekTo],
  );

  const skipBackward = useCallback(
    (seconds: number = 10) => {
      seekTo(state.currentTime - seconds);
    },
    [state.currentTime, seekTo],
  );

  const setPlaybackRate = useCallback((rate: number) => {
    const audio = audioRef.current;
    if (audio) {
      audio.playbackRate = rate;
    }
    setState((prev) => ({ ...prev, playbackRate: rate }));
  }, []);

  const getCurrentProgress = useCallback(() => {
    if (!audioRef.current || !state.duration) return 0;
    return audioRef.current.currentTime / state.duration;
  }, [state.duration]);

  return {
    state,
    audioRef,
    togglePlayPause,
    seekTo,
    skipForward,
    skipBackward,
    setPlaybackRate,
    getCurrentProgress,
  };
};
